// InvoiceGridList.tsx
import moment from 'moment';
import React from 'react';
import { Spinner } from 'react-bootstrap';
import { BiRefresh } from 'react-icons/bi';
import { FaCheck, FaExclamationTriangle, FaInfoCircle, FaEnvelope, FaFileAlt } from 'react-icons/fa';
import { KTSVG } from '../../..//_metronic/helpers';
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp';
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent';
import NoRecordFound from '../../../components/noRecord/NoRecordFound';
import labelKey from '../../localization/label.json';
import { constraintConfig } from '../../../constraintConfig';

interface InvoiceGridListProps {
    data: any[];
    isLoading: boolean;
    selectedInvoiceId: number | null;
    onRowClick: (item: any) => void;
    onDelete: (id: number) => void;
    onRetry: (id: number) => void;
    onViewFile: (fileURL: string) => void;
    heicPreviewUrls: { [key: string]: string };
}

// Helper function to check if file is MSG or EML
const isMsgOrEml = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lowerURL = fileURL.toLowerCase();
    return lowerURL.endsWith('.msg') || lowerURL.endsWith('.eml');
};

// Helper function to check if file is HEIC
const isHeicFile = (fileURL: string): boolean => {
    if (!fileURL) return false;
    const lowerURL = fileURL.toLowerCase();
    return lowerURL.endsWith('.heic') || lowerURL.endsWith('.heif');
};

// Helper function to get file extension
const getFileExtension = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const ext = fileURL.toLowerCase().split('.').pop();
    return ext?.toUpperCase() || 'File';
};

// Helper function to get filename from URL
const getFileNameFromURL = (fileURL: string): string => {
    if (!fileURL) return 'File';
    const parts = fileURL.split('/');
    return parts[parts.length - 1] || 'File';
};

const InvoiceGridList: React.FC<InvoiceGridListProps> = ({
    data,
    isLoading,
    selectedInvoiceId,
    onRowClick,
    onDelete,
    onRetry,
    onViewFile,
    heicPreviewUrls,
}) => {
    if (isLoading) {
        return <LoadingComponent />;
    }

    if (!data || data.length === 0) {
        return <NoRecordFound />;
    }

    return (
        <div className="row">
            {data.map((item: any, index: number) => {
                const isMsgOrEmlFile = isMsgOrEml(item.fileURL);
                const isHeicFileCheck = isHeicFile(item.fileURL);
                const fileName = getFileNameFromURL(item.fileURL);
                const heicPreviewUrl = heicPreviewUrls[item.invoiceDataDraftId];

                return (
                    <div className="col-md-6 col-xl-6 mt-4" key={index}>
                        <div 
                            className="card card-xl-stretch mb-xl-8 cursor-pointer" 
                            style={{ 
                                border: selectedInvoiceId === item.invoiceDataDraftId ? "2px solid #009ef7" : "1px solid #cccccc",
                                transition: "border-color 0.2s ease"
                            }}
                            onClick={() => onRowClick(item)}
                        >
                            <div className='position-relative' style={{ height: 350 }}>
                                {/* Status badge */}
                                <div className='position-absolute my-3 mx-3 z-index-1' style={{ left: 0, bottom: 0 }}>
                                    {item.ocrReadStatus === 0 ?
                                        <span className="btn btn-sm btn-primary fs-7 fw-semibold w-max-content">{labelKey.processing}... <Spinner animation="border" variant="white" size="sm" /> </span>
                                        : item.ocrReadStatus === 1 ?
                                            <span className="btn btn-sm btn-success fs-7 fw-semibold w-max-content">{labelKey.readyToUse} <FaCheck className='ms-2' /> </span>
                                            : item.ocrReadStatus === 2 ?
                                                <span className="btn btn-sm btn-danger fs-7 fw-semibold w-max-content">{labelKey.failed} <FaExclamationTriangle className='ms-2' /> </span>
                                                : item.ocrReadStatus === 3 ?
                                                    <span className="btn btn-sm btn-secondary fs-7 fw-bold w-max-content">{labelKey.attached}</span>
                                                    : item.ocrReadStatus === 4 ?
                                                        <span className="btn btn-sm btn-success fs-7 fw-bold w-max-content">{labelKey.submitted}</span>
                                                        : item.ocrReadStatus === 5 ?
                                                            <span className="btn btn-sm btn-warning fs-7 fw-bold w-max-content">{labelKey.alreadyUsed} </span>
                                                            : item.ocrReadStatus === 6 ?
                                                                <span className="btn btn-sm btn-info fs-7 fw-bold w-max-content">{labelKey.futureReceipt} </span>
                                                                : item.ocrReadStatus === 7 ?
                                                                    <span className="btn btn-sm btn-info fs-7 fw-bold w-max-content">{labelKey.cCReceipt} </span>
                                                                    : null
                                    }
                                </div>

                                {/* Delete button */}
                                {item?.expenseItemId === null || (item.ocrReadStatus !== 3 && item.ocrReadStatus !== 4) ?
                                    <div
                                        className='btn btn-icon btn-bg-light hover-danger btn-sm position-absolute m-4'
                                        style={{ left: 0, zIndex: 12, top: 6 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete(item?.invoiceDataDraftId);
                                        }}
                                    >
                                        <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3 text-hover-danger' svgClassName='svg-danger' />
                                    </div>
                                    : null}

                                {/* Retry button */}
                                {item?.noOfRetry <= 2 && item?.ocrReadStatus === 2 &&
                                    <div
                                        data-tooltip-id="retryInvoice" data-tooltip-content='Retry Invoice'
                                        className='btn btn-icon btn-bg-warning btn-sm position-absolute m-4'
                                        style={{ right: 0, zIndex: 12, top: 6 }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onRetry(item?.invoiceDataDraftId);
                                        }}
                                    >
                                        <BiRefresh className='h-15px w-15px' />
                                    </div>
                                }

                                {/* File display logic */}
                                {item.fileURL?.endsWith('.pdf') ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        style={{ cursor: "pointer", position: "relative", width: "100%", height: "100%" }}
                                    >
                                        <embed
                                            src={`${item.fileURL}#toolbar=0&navpanes=0&scrollbar=0`}
                                            type="application/pdf"
                                            width="100%"
                                            height="350px"
                                            className="cursor-pointer"
                                        />
                                    </div>
                                ) : isMsgOrEmlFile ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        style={{ cursor: "pointer", position: "relative", width: "100%", height: "100%" }}
                                    >
                                        <div className="d-flex flex-column align-items-center justify-content-center p-3" style={{ height: "350px", backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0", overflow: "hidden" }}>
                                            <FaEnvelope className="text-primary" style={{ fontSize: "48px", flexShrink: 0 }} />
                                            <h6 className="mt-3 text-center px-2 w-100" style={{ wordWrap: "break-word", overflowWrap: "break-word", wordBreak: "break-word", maxWidth: "100%", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "1.3" }}>
                                                {fileName}
                                            </h6>
                                            <span className="badge bg-primary mt-2" style={{ flexShrink: 0 }}>{getFileExtension(item.fileURL)}</span>
                                            <p className="text-muted mt-2 mb-0" style={{ flexShrink: 0 }}><small>Click to view</small></p>
                                        </div>
                                    </div>
                                ) : isHeicFileCheck ? (
                                    <div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        style={{ cursor: "pointer", position: "relative", width: "100%", height: "100%" }}
                                    >
                                        {heicPreviewUrl ? (
                                            <img className="card-img-top w-100 cursor-pointer" src={heicPreviewUrl} height={350} onClick={(e) => {
                                                e.stopPropagation();
                                                onViewFile(item?.fileURL);
                                            }} alt="HEIC preview" style={{ objectFit: 'contain' }} />
                                        ) : (
                                            <div className="d-flex flex-column align-items-center justify-content-center p-3" style={{ height: "350px", backgroundColor: "#f8f9fa", border: "1px solid #e0e0e0", overflow: "hidden" }}>
                                                <FaFileAlt className="text-warning" style={{ fontSize: "48px", flexShrink: 0 }} />
                                                <h6 className="mt-3 text-center px-2 w-100" style={{ wordWrap: "break-word", overflowWrap: "break-word", wordBreak: "break-word", maxWidth: "100%", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", textOverflow: "ellipsis", lineHeight: "1.3" }}>
                                                    {fileName}
                                                </h6>
                                                <span className="badge bg-warning mt-2" style={{ flexShrink: 0 }}>{getFileExtension(item.fileURL)}</span>
                                                <p className="text-muted mt-2 mb-0" style={{ flexShrink: 0 }}><small>Converting preview...</small></p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <img className="card-img-top w-100 cursor-pointer" src={item.fileURL}
                                        height={350}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewFile(item?.fileURL);
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = 'media/svg/files/blank-image.svg';
                                        }}
                                        alt="Card image cap"
                                    />
                                )}
                            </div>

                            <div className="p-2">
                                {/* Card details */}
                                <div className='d-flex align-items-start gap-2 mt-1'>
                                    <h5 className="card-title fs-7 m-0 w-100px">Vendor:</h5>
                                    <p className='mb-0 text-truncate'>{item.vendorName || 'N/A'}</p>
                                </div>
                                <div className='d-flex align-items-start gap-2 mt-1'>
                                    <h5 className="card-title fs-7 m-0 w-100px">Amount:</h5>
                                    <p className='mb-0'><CurrencyComp amount={item.amount} /></p>
                                </div>
                                <div className='d-flex align-items-start gap-2 mt-1'>
                                    <h5 className="card-title fs-7 m-0 w-100px">Receipt Date:</h5>
                                    <p className='mb-0'>{moment(item.invoiceDate).format('MM-DD-YYYY')}</p>
                                </div>
                                {(item?.ocrReadStatus === 3 || item?.ocrReadStatus === 4) && item.trackingId && (
                                    <div className='d-flex align-items-start gap-2 mt-1'>
                                        <h5 className="card-title fs-7 m-0 w-100px">Tracking ID:</h5>
                                        <p className='mb-0 text-primary text-truncate'>{item.trackingId}</p>
                                    </div>
                                )}
                            </div>

                            <div className="card-footer py-1">
                                <span className="text-muted fs-8">Click to view details</span>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default InvoiceGridList;